/*
 * Route guards.
 *   RequireAuth  signed-in only; otherwise /login?next=<path>. Keeps rendering cached data when the
 *                session expires mid-visit (the re-login dialog takes over instead of a redirect). When the
 *                organisation requires 2FA and the member has none (`session.twoFactorSetupRequired`),
 *                every page sends them to /settings/security until they set it up.
 *   GuestOnly    signed-out only (sign in, sign up); signed-in people go to `next` or "/".
 *   RootRedirect "/": signed out → /signup (first visit) or /login (returning); signed in → /welcome when
 *                the organisation has no server yet, else /overview.
 */
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router'
import { isApiError } from '@/api/client'
import { useSession } from '@/api/hooks/auth'
import { ErrorState } from '@/components/ui'
import { storage } from '@/lib/storage'
import { SplashScreen } from '@/components/shell/SplashScreen'

export const RETURNING_KEY = 'bpanel.returning'
const TWO_FACTOR_SETUP_PATH = '/settings/security'

export function RequireAuth() {
  const q = useSession()
  const location = useLocation()
  const expired = isApiError(q.error) && q.error.status === 401

  useEffect(() => {
    if (q.data) storage.set(RETURNING_KEY, '1')
  }, [q.data])

  if (q.data?.twoFactorSetupRequired && location.pathname !== TWO_FACTOR_SETUP_PATH)
    return <Navigate to={TWO_FACTOR_SETUP_PATH} replace />
  if (q.data) return <Outlet />
  if (q.isPending) return <SplashScreen />
  if (q.isError && !expired)
    return <ErrorState error={q.error} onRetry={() => q.refetch()} className="min-h-dvh justify-center" />
  const next = location.pathname + location.search
  return <Navigate to={`/login${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />
}

/** Only allows same-app relative paths as a post-login target. */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return '/'
  return next
}

export function GuestOnly() {
  const q = useSession()
  const [params] = useSearchParams()
  if (q.isPending) return <SplashScreen />
  if (q.data) return <Navigate to={safeNext(params.get('next'))} replace />
  return <Outlet />
}

export function RootRedirect() {
  const q = useSession()
  if (q.isPending) return <SplashScreen />
  if (!q.data) return <Navigate to={storage.get(RETURNING_KEY) ? '/login' : '/signup'} replace />
  return <Navigate to={q.data.organization.serverCount === 0 ? '/welcome' : '/overview'} replace />
}
